/**
 * Object Inspector Page
 *
 * Admin debugging tool to inspect any database object by its global ID.
 * Accessible at /admin/obj/:globalId
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useObjectInspectorGraphQL } from '../hooks/useObjectInspectorGraphQL';
import { ObjectDetailsGrid } from '../components/ObjectDetailsGrid';
import { GraphQLRelationships } from '../components/GraphQLRelationships';
import { Spinner } from '../components/atoms/Spinner/Spinner';
import { objUrl, isValidGlobalId } from '../utils/urls';

export function ObjectInspectorPage() {
  const { globalId } = useParams<{ globalId: string }>();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState(globalId || '');
  const { data, isLoading, error } = useObjectInspectorGraphQL(globalId);

  // Update input value when route param changes
  useEffect(() => {
    setInputValue(globalId || '');
  }, [globalId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedValue = inputValue.trim();
    if (trimmedValue && isValidGlobalId(trimmedValue)) {
      navigate(objUrl(trimmedValue));
    } else if (trimmedValue) {
      // Invalid global ID format - still navigate to show error state
      navigate(`/admin/obj/${trimmedValue}`);
    } else {
      navigate('/admin/obj');
    }
  };

  return (
    <div className="p-8 overflow-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2">Object Inspector</h1>
        <p className="text-muted">
          Debug tool for viewing complete object details by global ID
        </p>
      </div>

      {/* Input Form - Always Visible */}
      <div className="mb-6 p-4 bg-surface-1 border border-border rounded-lg">
        <form onSubmit={handleSubmit} className="flex items-center gap-4">
          <label htmlFor="global-id-input" className="text-sm font-medium text-muted">
            Global ID:
          </label>
          <input
            id="global-id-input"
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Enter global ID (e.g., us1a, co4m, ph2b)"
            className="flex-1 px-3 py-2 font-mono text-sm bg-background text-foreground border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-primary-foreground font-medium rounded-md hover:bg-primary/90 transition-colors"
          >
            Inspect
          </button>
        </form>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-6 bg-danger/10 border border-danger/30 rounded-lg">
          <h2 className="text-xl font-semibold text-danger mb-2">Error</h2>
          <p className="text-foreground">
            {error.message || 'Failed to load object'}
          </p>
          <div className="mt-4 text-sm text-muted">
            <p>Possible reasons:</p>
            <ul className="list-disc list-inside mt-2">
              <li>Invalid global ID format</li>
              <li>Unknown object type prefix</li>
              <li>Object not found in database</li>
              <li>Permission denied (admin access required)</li>
            </ul>
          </div>
        </div>
      )}

      {/* Success State - Display Object */}
      {data && (
        <div className="space-y-6">
          {/* Object Metadata */}
          <div className="p-4 bg-surface-1 border border-border rounded-lg">
            <h2 className="text-xl font-semibold text-foreground mb-3">Object Metadata</h2>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted">Type:</span>
                <p className="font-semibold text-foreground mt-1">{data.type}</p>
              </div>
              <div>
                <span className="text-muted">Numeric ID:</span>
                <p className="font-mono font-semibold text-foreground mt-1">{data.id}</p>
              </div>
              <div>
                <span className="text-muted">Global ID:</span>
                <p className="font-mono font-semibold text-primary mt-1">{data.globalId}</p>
              </div>
            </div>
          </div>

          {/* Object Fields */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">Object Fields</h2>
            <ObjectDetailsGrid object={data.object} objectType={data.type} />
          </div>

          {/* Related Objects */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-3">Related Objects</h2>
            <p className="text-sm text-muted mb-4">
              Objects referenced by or containing this object
            </p>
            <GraphQLRelationships
              objectData={data.object}
              isLoading={isLoading}
            />
          </div>
        </div>
      )}

      {/* No Global ID Provided */}
      {!globalId && !isLoading && !error && (
        <div className="p-6 bg-surface-1 border border-border rounded-lg text-center">
          <p className="text-muted">
            Enter a global ID above to inspect an object.
          </p>
          <div className="text-sm text-muted mt-4">
            <p className="mb-2">Example IDs:</p>
            <div className="flex justify-center gap-4 font-mono">
              <span>us1a (user)</span>
              <span>co4m (collection)</span>
              <span>ph2b (photo)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
